import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import styled from "@emotion/styled";
import truncate from "lodash/truncate";
import { useBanks } from "@rentablo/api/apiv1";
import { CreateOrUpdateAccountParams } from "@api";
import { GET_ACCOUNTS } from "@api/cacheKeys";
import { PageLayout } from "@components";
import { Banner, LoadingProgressBar } from "@rentablo/components";
import { useAccountsId, useUpdateAccount } from "@rentablo/api/apiv1";
import { ReferenceAccountsCard } from "@features/depots/referenceAccount/components/ReferenceAccountsCard";
import { AccountEditCard } from "./AccountEditCard";
import { AccountEditHeader } from "./AccountEditHeader";
import { CreateAndEditAccountForm } from "./CreateAndEditAccountForm";
import { CreateAndUpdateResultMessage } from "./CreateAndUpdateResultMessage";
import { DangerZoneCard } from "./DangerZoneCard";
import { LinkedDepotsCard } from "./LinkedDepotsCard";

const Content = styled("div")({
  maxWidth: "760px",
});

interface CreateAndEditAccountProps {
  accountId: string;
  type: "account" | "depot";
}

export const CreateAndEditAccount = (props: CreateAndEditAccountProps) => {
  const { accountId, type } = props;
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const parsedAccountId = Number.parseInt(accountId, 10);

  const [accountValues, setAccountValues] =
    useState<CreateOrUpdateAccountParams>({
      name: "",
      type: "01_depot",
    });

  const { mutateAsync, error, status } = useUpdateAccount({
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: [GET_ACCOUNTS],
      });
      enqueueSnackbar(t("addManuallyPage.form.messages.success"), {
        variant: "success",
        autoHideDuration: 3000,
      });
    },
  });
  const { data: account, isLoading } = useAccountsId(
    {
      id: parsedAccountId,
    },
    {
      onSuccess: (account) => {
        setAccountValues({
          name: account?.name ?? "",
          type: account?.type ?? "01_depot",
          bankName: account?.bankName,
        });
      },
    }
  );

  const { data: searchBankResponse } = useBanks(
    {
      search: account?.bankCode as string,
    },
    {
      enabled: Boolean(account?.bankCode),
    }
  );

  const [bank] = searchBankResponse?.banks ?? [];

  const handleUpdateAccountValues = (
    params: Partial<CreateOrUpdateAccountParams>
  ) => {
    setAccountValues((values) => ({ ...values, ...params }));
  };

  const handleCommit = (params: Partial<CreateOrUpdateAccountParams> = {}) => {
    const next = { ...accountValues, ...params };
    const isBankChange = "bankName" in params || "bic" in params;
    if (
      !isBankChange &&
      next.name === account?.name &&
      next.type === account?.type
    ) {
      return;
    }
    mutateAsync({
      id: parsedAccountId,
      createOrUpdateAccountParams: next,
    });
  };

  const url = type === "depot" ? "/depots" : "/accounts";
  const accountName = account?.name ?? "";

  if (isLoading) {
    return <LoadingProgressBar data-testid="account-edit-loading" />;
  }

  if (!account) {
    return (
      <Banner
        type="error"
        title={t("notFound.title")}
        text={t("notFound.text")}
        displayDefaultIcon
        data-testid="account-edit-not-found"
      />
    );
  }

  return (
    <PageLayout
      breadcrumbs={[
        { to: "/", text: t("breadcrumbs.home") },
        {
          to: "/depots-accounts",
          text: t("breadcrumbs.depotsAccounts"),
        },
        {
          to: `${url}/${accountId}`,
          text: truncate(accountName),
        },
        {
          text: t("createAndEditAccount.editBreadcumb"),
        },
      ]}
    >
      <Content data-testid="account-edit-form">
        <AccountEditHeader account={account} />

        <CreateAndUpdateResultMessage
          message={error?.response?.data?.message}
          type={status === "error" ? "error" : undefined}
        />

        <AccountEditCard
          title={t("createAndEditAccount.details.title")}
          description={t("createAndEditAccount.details.description")}
          testId="account-details-card"
        >
          <CreateAndEditAccountForm
            accountValues={accountValues}
            hideFields={{
              bank: Boolean(account?.bankConnectionType),
              name: false,
              type: true,
              currency: true,
            }}
            defaultBank={bank}
            onUpdateAccountValues={handleUpdateAccountValues}
            onCommit={handleCommit}
          />
        </AccountEditCard>

        {account?.type === "02_cash" && (
          <LinkedDepotsCard accountId={parsedAccountId} />
        )}

        {account?.type === "01_depot" && (
          <ReferenceAccountsCard depotId={parsedAccountId} />
        )}

        <DangerZoneCard account={account} type={type} />
      </Content>
    </PageLayout>
  );
};
